import React, { useState, useEffect } from 'react';
import { safeGetItem, safeSetItem } from './data/storage';

interface FeeEstimatorProps {
  nationality: string;
  visitingPurpose: string;
}

const DEFAULT_PASSWORD = 'mySky@2025';
const EXCHANGE_RATE_LKR = 340; // 1 USD = 340 LKR

export const FeeEstimator: React.FC<FeeEstimatorProps> = ({ nationality, visitingPurpose }) => {
  const [isUnlocked, setIsUnlocked] = useState<boolean>(() => {
    return safeGetItem('visaAgentUnlocked') === 'true';
  });
  const [showModal, setShowModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [showPasswordText, setShowPasswordText] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    safeSetItem('visaAgentUnlocked', isUnlocked ? 'true' : 'false');
    window.dispatchEvent(new Event('agentStatusChanged'));
  }, [isUnlocked]);

  useEffect(() => {
    const handleOpenModal = () => setShowModal(true);
    window.addEventListener('openAgentAuthModal', handleOpenModal);
    return () => window.removeEventListener('openAgentAuthModal', handleOpenModal);
  }, []);

  const handleUnlockClick = () => {
    const cleanInput = passwordInput.trim();
    const envPass = process.env.REACT_APP_AGENT_PASSWORD || DEFAULT_PASSWORD;

    if (
      cleanInput === DEFAULT_PASSWORD ||
      cleanInput.toLowerCase() === DEFAULT_PASSWORD.toLowerCase() ||
      cleanInput === envPass
    ) {
      setIsUnlocked(true);
      setSuccessMsg('✅ Password Accepted! Agent Mode Unlocked.');
      setErrorMsg('');
      setTimeout(() => {
        setShowModal(false);
        setPasswordInput('');
        setSuccessMsg('');
      }, 400);
    } else {
      setErrorMsg('❌ Incorrect password!');
    }
  };

  const handleLock = () => {
    setIsUnlocked(false);
  };

  if (!isUnlocked) {
    if (!showModal) return null;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(4px)',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '1rem',
        }}
      >
        <div
          style={{
            background: '#ffffff',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '420px',
            width: '100%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
          }}
        >
          <h3 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#0f172a', marginBottom: '0.35rem' }}>
            🔒 Agent Mode Login
          </h3>
          <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '1.25rem' }}>
            Please enter the agent password to unlock live fee calculations.
          </p>

          <div>
            <div style={{ position: 'relative', marginBottom: '0.5rem' }}>
              <input
                type={showPasswordText ? 'text' : 'password'}
                placeholder="Enter Password..."
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleUnlockClick();
                  }
                }}
                autoFocus
                style={{
                  width: '100%',
                  padding: '0.75rem 2.5rem 0.75rem 1rem',
                  borderRadius: '8px',
                  border: errorMsg ? '2px solid #ef4444' : '1px solid #cbd5e1',
                  fontSize: '0.95rem',
                }}
              />
              <button
                type="button"
                onClick={() => setShowPasswordText(!showPasswordText)}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1rem',
                }}
              >
                {showPasswordText ? '🙈' : '👁️'}
              </button>
            </div>

            {errorMsg && (
              <div
                style={{
                  background: '#fef2f2',
                  border: '1px solid #fca5a5',
                  color: '#dc2626',
                  padding: '0.65rem',
                  borderRadius: '8px',
                  fontSize: '0.825rem',
                  marginBottom: '0.75rem',
                  fontWeight: '700',
                }}
              >
                {errorMsg}
              </div>
            )}

            {successMsg && (
              <div
                style={{
                  background: '#f0fdf4',
                  border: '1px solid #86efac',
                  color: '#15803d',
                  padding: '0.65rem',
                  borderRadius: '8px',
                  fontSize: '0.825rem',
                  marginBottom: '0.75rem',
                  fontWeight: '700',
                }}
              >
                {successMsg}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setErrorMsg('');
                  setSuccessMsg('');
                  setPasswordInput('');
                }}
                className="btn-secondary"
                style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUnlockClick}
                className="btn-primary"
                style={{ padding: '0.5rem 1.25rem', fontSize: '0.85rem' }}
              >
                Unlock Agent Mode 🔓
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate specific fee if purpose is selected
  const currentMonth = new Date().getMonth() + 1; // 1 to 12
  let feeUSD = 80;

  if (visitingPurpose) {
    const pLower = visitingPurpose.toLowerCase();
    if (pLower.includes('30 days')) {
      if (currentMonth >= 4 && currentMonth <= 6) {
        feeUSD = 10;
      } else {
        feeUSD = 25;
      }
    } else if (pLower.includes('1 year')) {
      feeUSD = 40;
    } else if (pLower.includes('5 year')) {
      feeUSD = 80;
    } else if (pLower.includes('transit')) {
      feeUSD = 20;
    } else if (pLower.includes('business') || pLower.includes('medical') || pLower.includes('conference')) {
      feeUSD = 80;
    }

    if (['JAPAN', 'SINGAPORE'].includes(nationality?.toUpperCase())) {
      feeUSD = 25;
    }
  }

  const feeLKR = Math.round(feeUSD * EXCHANGE_RATE_LKR);

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        border: '2px solid #22c55e',
        borderRadius: '12px',
        padding: '1.25rem 1.5rem',
        marginTop: '1.25rem',
        boxShadow: '0 4px 14px rgba(34, 197, 94, 0.2)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span
            style={{
              background: '#166534',
              color: '#ffffff',
              padding: '0.25rem 0.75rem',
              borderRadius: '9999px',
              fontSize: '0.75rem',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            🟢 AGENT MODE UNLOCKED
          </span>
          <span style={{ fontSize: '0.8rem', color: '#15803d', fontWeight: '600' }}>
            Official Govt Fares (1 USD = 340 LKR)
          </span>
        </div>

        <button
          type="button"
          onClick={handleLock}
          style={{
            background: '#dc2626',
            color: '#ffffff',
            border: 'none',
            padding: '0.35rem 0.75rem',
            borderRadius: '6px',
            fontSize: '0.75rem',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          🔒 Lock Agent Mode
        </button>
      </div>

      {visitingPurpose ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', paddingTop: '0.5rem', borderTop: '1px solid #bbf7d0' }}>
          <div>
            <div style={{ fontSize: '1rem', fontWeight: '800', color: '#14532d' }}>
              Calculated e-Visa Government Fee
            </div>
            <div style={{ fontSize: '0.825rem', color: '#166534', marginTop: '0.15rem' }}>
              Nationality: <strong>{nationality || 'All'}</strong> | Selected Purpose: <strong>{visitingPurpose}</strong>
            </div>
          </div>

          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '1.85rem', fontWeight: '900', color: '#15803d', lineHeight: 1.1 }}>
              ${feeUSD} <span style={{ fontSize: '1rem', color: '#166534', fontWeight: '700' }}>USD</span>
            </div>
            <div style={{ fontSize: '0.95rem', fontWeight: '700', color: '#166534', marginTop: '0.2rem' }}>
              ≈ LKR {feeLKR.toLocaleString()} Rs
            </div>
          </div>
        </div>
      ) : (
        <div style={{ paddingTop: '0.5rem', borderTop: '1px solid #bbf7d0' }}>
          <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#14532d', marginBottom: '0.5rem' }}>
            📊 Standard e-Visa Government Fee Schedule (1 USD = 340 LKR):
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.5rem' }}>
            <div style={{ background: '#ffffff', padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>e-Tourist 30 Days</div>
              <div style={{ fontSize: '1.1rem', fontWeight: '800', color: '#15803d' }}>
                ${currentMonth >= 4 && currentMonth <= 6 ? '10' : '25'} USD <span style={{ fontSize: '0.75rem', color: '#166534' }}>(≈ LKR {((currentMonth >= 4 && currentMonth <= 6 ? 10 : 25) * 340).toLocaleString()})</span>
              </div>
            </div>
            <div style={{ background: '#ffffff', padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>e-Tourist 1 Year</div>
              <div style={{ fontSize: '1.1rem', fontWeight: '800', color: '#15803d' }}>
                $40 USD <span style={{ fontSize: '0.75rem', color: '#166534' }}>(≈ LKR {(40 * 340).toLocaleString()})</span>
              </div>
            </div>
            <div style={{ background: '#ffffff', padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>e-Tourist 5 Years</div>
              <div style={{ fontSize: '1.1rem', fontWeight: '800', color: '#15803d' }}>
                $80 USD <span style={{ fontSize: '0.75rem', color: '#166534' }}>(≈ LKR {(80 * 340).toLocaleString()})</span>
              </div>
            </div>
            <div style={{ background: '#ffffff', padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>e-Business / Medical</div>
              <div style={{ fontSize: '1.1rem', fontWeight: '800', color: '#15803d' }}>
                $80 USD <span style={{ fontSize: '0.75rem', color: '#166534' }}>(≈ LKR {(80 * 340).toLocaleString()})</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
